// ============================================================================
// Bid Workbook — the structured "estimate" behind a bid.
// ----------------------------------------------------------------------------
// This models the same data as the client Excel bid workbook (one sheet per
// construction phase, each with an EQUIPMENT and a LABOUR table, rolled up into
// a SUMMARY) — but in a shape that drives a fast, inline editing UI instead of
// 22 fragile spreadsheet tabs.
//
// Every line item uses one unified formula:
//     total = qty × hrs × days × rate
// which matches both the equipment rows (qty × hrs × days × ISR rate) and the
// labour rows (qty × hrs × rate/hr × days) in the original workbook.
// ============================================================================

export type LineItemKind = 'equipment' | 'labour';

/** Whether a line is performed in-house (ISR) or by a subcontractor (OSR). */
export type LineItemSource = 'inhouse' | 'sub';

export interface BidLineItem {
  id: string;
  description: string;
  kind: LineItemKind;
  source: LineItemSource;
  qty: number;
  /** Hours per day (or per unit) — equipment & labour both use this. */
  hrs: number;
  days: number;
  /**
   * Dollar rate (per hour for labour, per hour/day for equipment). Picking a catalog item copies
   * its rate here and it is then FROZEN: later catalog edits never reprice an existing bid.
   */
  rate: number;
  /**
   * Catalog provenance. Set when the row was chosen from an admin catalog (P3); left undefined for
   * a hand-typed row. Kept only so a bid can be traced back to its source — the money always comes
   * from the frozen `rate` above, never from a live catalog lookup.
   */
  equipmentItemId?: string;
  labourRoleId?: string;
  costCodeId?: string;
}

/**
 * One subcontract quote on a phase (gap 3.9) — the CLEARING (SUB) comparison row.
 * `quoteValue` is author-supplied vendor money: stripped server-side for blind readers
 * (undefined here), and undefined also means "quote requested, price not yet in" — money()
 * renders both honestly as a dash. At most one row per phase carries isSuccessful.
 */
export interface SubQuote {
  vendor: string;
  quoteValue?: number;
  qualifiers: string;
  isSuccessful: boolean;
}

export interface BidPhase {
  id: string;
  name: string;
  /** Metres of pipeline this phase covers. */
  distanceM: number;
  /** Crew production rate, metres per day. Duration is derived from this. */
  productionPerDayM: number;
  specialConditions: string;
  /** Sheet-level profit override (%, three-tier margin resolution). Stored + resolved; not
   * applied to totals until the WGL engine (gap review 1.3/1.4). Absent for blind readers. */
  profitRateOverride?: number;
  /** Its overhead sibling — no UI input yet (the WGL engine's field), but it MUST round-trip:
   * the review proved a stored value wiped by the next UI save when the mapping dropped it. */
  overheadRateOverride?: number;
  /** The admin template that seeded this phase (gap review 1.2) — provenance, like line ids. */
  phaseTemplateId?: string;
  /**
   * The welding productivity calculator's saved inputs (gap 3.10): production/day =
   * jointLengthM × weldsPerDay; joints = distanceM / jointLengthM. Provenance for the stored
   * productionPerDayM — nothing recomputes the rate from them, so a later hand edit of the rate
   * supersedes them without contradiction. Set/cleared together (half a state derives nothing).
   */
  jointLengthM?: number;
  weldsPerDay?: number;
  /** Subcontract quote comparison (gap 3.9). Rides the phase so the replace-all round-trips
   * it — the same wipe-protection obligation as the overrides and calculator inputs. */
  subQuotes?: SubQuote[];
  items: BidLineItem[];
}

export interface BidWorkbook {
  /** Right-of-way / contract length in metres (drives cost-per-metre). */
  rowLengthM: number;
  /** Profit markup applied on top of base cost, as a percent (e.g. 30). */
  /** undefined when the server omitted it (financially-blind caller) — never coerced to 0. */
  profitPct?: number;
  phases: BidPhase[];
}

// ---------------------------------------------------------------------------
// Computation helpers (pure)
// ---------------------------------------------------------------------------

export function lineTotal(item: BidLineItem): number {
  const t = (item.qty || 0) * (item.hrs || 0) * (item.days || 0) * (item.rate || 0);
  return Number.isFinite(t) ? t : 0;
}

export function phaseDurationDays(phase: BidPhase): number {
  if (!phase.productionPerDayM || phase.productionPerDayM <= 0) return 0;
  return phase.distanceM / phase.productionPerDayM;
}

/**
 * Man-days for a phase: Σ qty × days over the LABOUR lines (gap 3.12). hrs deliberately
 * excluded — a man-day is a hand on site for a day; the workbook's "TOTAL MAN DAYS × 12 HRS"
 * derives hours FROM this. Sub labour counts (camp feeds people regardless of employer);
 * equipment lines never do (the operator is their own labour line). Mirrors
 * bid_calc.phase_man_days — parity pinned by the shared fixture.
 */
export function phaseManDays(phase: BidPhase): number {
  let t = 0;
  for (const item of phase.items) {
    if (item.kind === 'labour') t += (item.qty || 0) * (item.days || 0);
  }
  return Number.isFinite(t) ? t : 0;
}

/** Man-hours: Σ qty × hrs × days over labour lines — real hours off the lines, not the sheet's
 * hardcoded × 12. */
export function phaseManHours(phase: BidPhase): number {
  let t = 0;
  for (const item of phase.items) {
    if (item.kind === 'labour') t += (item.qty || 0) * (item.hrs || 0) * (item.days || 0);
  }
  return Number.isFinite(t) ? t : 0;
}

/** Per-phase value rounded to 2dp (half-up) — the resolution the server serializes at. */
const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * The rollup sums the ROUNDED per-phase values, matching the server's quantize-then-sum
 * (_man_day_rollup): the total must cross-foot with the rows a reader sees, and summing raw
 * floats here while the server sums 2dp strings let the two engines disagree by up to
 * 0.005 × phases (review). Parity-pinned by the shared fixture's manDayRollup cases.
 */
export function workbookManDays(wb: BidWorkbook): number {
  return wb.phases.reduce((sum, p) => sum + round2(phaseManDays(p)), 0);
}

export function workbookManHours(wb: BidWorkbook): number {
  return wb.phases.reduce((sum, p) => sum + round2(phaseManHours(p)), 0);
}

// ---------------------------------------------------------------------------
// Welding productivity calculator (gap 3.10)
// ---------------------------------------------------------------------------

/** production/day (m) = joint length × welds/day. 0 for missing/invalid inputs. */
export function weldingProductionPerDay(jointLengthM: number, weldsPerDay: number): number {
  const p = (jointLengthM || 0) * (weldsPerDay || 0);
  return Number.isFinite(p) && p > 0 ? p : 0;
}

/** Joint count for a distance, rounded UP — a part-length joint is still a joint to weld
 * (the workbook SUMMARY's `Joints` parameter). 0 when either input is missing. */
export function weldingJoints(distanceM: number, jointLengthM: number): number {
  if (!jointLengthM || jointLengthM <= 0 || !distanceM || distanceM <= 0) return 0;
  return Math.ceil(distanceM / jointLengthM);
}

// ---------------------------------------------------------------------------
// Pricing calculators (gap 4.1 — loads: "build the automated calculation";
// fluid: formula confirmed. Both answered 2026-08-07, recorded in the Tier 4 brief.)
//
// The calculators derive line COMPONENTS and apply them as an ordinary, visible line the
// estimator confirms — never a hidden multiplier on a total. Pricing itself stays the
// engine's one contract (qty × hrs × days × rate), so no new algebra enters either engine
// and the shared parity fixture deliberately gains no cases.
// ---------------------------------------------------------------------------

/**
 * Applied-line signatures. The DESCRIPTION is the calculator's provenance — unlike welding's
 * bare production rate, a line carries its description everywhere it goes, so no schema
 * change is needed. Two rules follow:
 *  * the text must never carry money (descriptions survive the financial strip; a rate in
 *    the words would leak to blind readers — the 'estimated at 18%' lesson);
 *  * a line that still wears the prefix belongs to its calculator (Apply updates it in
 *    place rather than appending a duplicate); renaming it is the estimator taking
 *    ownership, and the calculator leaves it alone from then on.
 */
export const MOB_LOADS_LINE_PREFIX = 'Mob/Demob loads — ';
export const FLUID_LINE_PREFIX = 'Hydro-test fluid — ';
/**
 * The MOBE-DEMOBE sheet prices TWO blocks — mobilization and demobilization, each loads ×
 * cost/load — so the loads calculator applies as a PAIR wearing these signatures (plan
 * mob-demob-sheet-parity, Build M). The spelling is the demo seed's own ("Mobilization — Legal
 * Loads"), so seeded bids are adopted in place rather than doubled; a line still wearing the
 * legacy combined MOB_LOADS_LINE_PREFIX is adopted into the Mobilization slot on the next
 * Apply, so no bid keeps a stale single line beside the pair.
 */
export const MOB_LINE_PREFIX = 'Mobilization — ';
export const DEMOB_LINE_PREFIX = 'Demobilization — ';

/** One calculator line landing in (or replacing within) a phase — the unit of a batched Apply.
 * A calculator that applies a PAIR must land both in ONE persist: two writes would double-save
 * the replace-all workbook and race each other onto the wire. */
export interface CalcLineUpsert {
  phaseId: string;
  line: BidLineItem;
  replaceItemId?: string;
}

/** The first line still wearing a calculator's signature — the update-in-place target. */
export function findLineByPrefix(
  phases: BidPhase[],
  prefix: string,
): { phase: BidPhase; item: BidLineItem } | null {
  for (const phase of phases) {
    const item = phase.items.find((it) => it.description.startsWith(prefix));
    if (item) return { phase, item };
  }
  return null;
}

/**
 * Hydro-test fluid volume: length × pipe-spec m³/m × fill factor, rounded to 3 dp (the qty
 * column's own precision). 0 for any missing/non-positive input — half a calculator state
 * derives nothing (the welding calculator's both-or-neither doctrine).
 */
export function fluidVolumeM3(lengthM: number, m3PerM: number, fillFactor: number): number {
  if (!Number.isFinite(lengthM) || lengthM <= 0) return 0;
  if (!Number.isFinite(m3PerM) || m3PerM <= 0) return 0;
  if (!Number.isFinite(fillFactor) || fillFactor <= 0) return 0;
  return Math.round(lengthM * m3PerM * fillFactor * 1000) / 1000;
}

export interface PhaseTotals {
  equipment: number; // in-house equipment
  labour: number; // in-house labour
  subcontract: number; // anything marked as sub
  total: number;
  costPerM: number;
}

export function phaseTotals(phase: BidPhase, rowLengthM: number): PhaseTotals {
  let equipment = 0;
  let labour = 0;
  let subcontract = 0;
  for (const item of phase.items) {
    const t = lineTotal(item);
    if (item.source === 'sub') subcontract += t;
    else if (item.kind === 'labour') labour += t;
    else equipment += t;
  }
  const total = equipment + labour + subcontract;
  const costPerM = rowLengthM > 0 ? total / rowLengthM : 0;
  return { equipment, labour, subcontract, total, costPerM };
}

export interface WorkbookTotals {
  equipment: number;
  labour: number;
  subcontract: number;
  baseCost: number;
  profit: number;
  bidTotal: number;
  costPerM: number;
}

export function workbookTotals(wb: BidWorkbook): WorkbookTotals {
  let equipment = 0;
  let labour = 0;
  let subcontract = 0;
  for (const phase of wb.phases) {
    const t = phaseTotals(phase, wb.rowLengthM);
    equipment += t.equipment;
    labour += t.labour;
    subcontract += t.subcontract;
  }
  const baseCost = equipment + labour + subcontract;
  const profit = baseCost * ((wb.profitPct || 0) / 100);
  const bidTotal = baseCost + profit;
  const costPerM = wb.rowLengthM > 0 ? bidTotal / wb.rowLengthM : 0;
  return { equipment, labour, subcontract, baseCost, profit, bidTotal, costPerM };
}

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------

let idCounter = 0;
export function newId(prefix = 'li'): string {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

export function emptyLineItem(kind: LineItemKind): BidLineItem {
  return {
    id: newId(kind === 'labour' ? 'lab' : 'eq'),
    description: '',
    kind,
    source: 'inhouse',
    qty: 1,
    hrs: kind === 'labour' ? 12 : 1,
    days: 1,
    rate: 0,
  };
}

/** A blank workbook used when a bid has no estimate yet. */
export function buildEmptyWorkbookSafe(): BidWorkbook {
  return { rowLengthM: 0, profitPct: 30, phases: [] };
}

export function emptyPhase(name = 'New Phase'): BidPhase {
  return {
    id: newId('ph'),
    name,
    distanceM: 0,
    productionPerDayM: 0,
    specialConditions: '',
    items: [],
  };
}

/** Standard pipeline construction phases, for one-click "add phase". */
export const PHASE_LIBRARY: string[] = [
  'Mobilization / Demobilization',
  'Clearing',
  'Signs and Safety',
  'Access and Grade',
  'Ditchline Set Skids',
  'Stringing & Pipehaul',
  'Engineering',
  'Bend & Set-up',
  'Mainline Welding',
  'Coating',
  'Ditch',
  'Lower-in',
  'Tie-ins',
  'Special Crew / Bore Support',
  'Backfill',
  'Clean-up',
  'Pig & Test',
  'Mechanic / Fuel / Service',
  'On-Site Trucking',
  'Office / Warehouse / Yard',
  'Safety',
  'Supervision',
];

// ---------------------------------------------------------------------------
// Seed workbook — real numbers lifted from the client bid workbook .xls so the
// demo opens with a fully-populated, self-consistent estimate.
// ---------------------------------------------------------------------------

function eq(description: string, qty: number, hrs: number, days: number, rate: number, source: LineItemSource = 'inhouse'): BidLineItem {
  return { id: newId('eq'), description, kind: 'equipment', source, qty, hrs, days, rate };
}
function lab(description: string, qty: number, hrs: number, days: number, rate: number, source: LineItemSource = 'inhouse'): BidLineItem {
  return { id: newId('lab'), description, kind: 'labour', source, qty, hrs, days, rate };
}

// ---------------------------------------------------------------------------
// Generic scope-based workbook generator — produces a full, internally
// consistent estimate (every standard phase, crews, equipment) for a given
// pipeline length so any bid opens looking "completed".
// ---------------------------------------------------------------------------

interface CrewLine {
  desc: string;
  kind: LineItemKind;
  qty: number;
  hrs: number;
  rate: number;
  source?: LineItemSource;
}
interface OneTimeLine extends CrewLine {
  days: number;
}
interface PhaseTemplate {
  name: string;
  /** Crew production rate, metres/day — drives the phase duration. */
  prodPerDay: number;
  conditions?: string;
  /** Duration-based lines (days = phase duration). */
  crew: CrewLine[];
  /** One-off lines billed once (mobilization, rod, consumables, etc.). */
  oneTime?: OneTimeLine[];
}

const STD_PHASES: PhaseTemplate[] = [
  {
    name: 'Access and Grade',
    prodPerDay: 581,
    crew: [
      { desc: 'Operator Pick-up', kind: 'equipment', qty: 6, hrs: 1, rate: 300 },
      { desc: 'D6 Dozer', kind: 'equipment', qty: 2, hrs: 8, rate: 165 },
      { desc: '4x4 Crew Cab', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: '350 Hoes', kind: 'equipment', qty: 4, hrs: 8, rate: 175 },
      { desc: 'Foreman', kind: 'labour', qty: 1, hrs: 12, rate: 90 },
      { desc: 'Operator', kind: 'labour', qty: 6, hrs: 12, rate: 80 },
      { desc: 'Labourer', kind: 'labour', qty: 2, hrs: 12, rate: 60 },
    ],
  },
  {
    name: 'Stringing & Pipehaul',
    prodPerDay: 2320,
    crew: [
      { desc: '4x4 Crew Cab', kind: 'equipment', qty: 2, hrs: 1, rate: 300 },
      { desc: '350 Hoe', kind: 'equipment', qty: 1, hrs: 8, rate: 175 },
      { desc: 'Operators Pick-up', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Foreman', kind: 'labour', qty: 1, hrs: 12, rate: 90 },
      { desc: 'Operator', kind: 'labour', qty: 1, hrs: 12, rate: 80 },
      { desc: 'Labourer', kind: 'labour', qty: 5, hrs: 12, rate: 60 },
      { desc: 'Pipehaul Trucks', kind: 'equipment', qty: 10, hrs: 1, rate: 190, source: 'sub' },
    ],
  },
  {
    name: 'Bend & Set-up',
    prodPerDay: 2320,
    crew: [
      { desc: '561 Boom', kind: 'equipment', qty: 1, hrs: 8, rate: 95 },
      { desc: '4x4 Crew Cab', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Bending Machine', kind: 'equipment', qty: 1, hrs: 1, rate: 750 },
      { desc: 'Foreman', kind: 'labour', qty: 1, hrs: 12, rate: 90 },
      { desc: 'Intermediate Operators', kind: 'labour', qty: 1, hrs: 12, rate: 75 },
      { desc: 'Labourer', kind: 'labour', qty: 2, hrs: 12, rate: 60 },
    ],
  },
  {
    name: 'Mainline Welding',
    prodPerDay: 1105,
    conditions: 'Welding rod and pressure welders subcontracted.',
    crew: [
      { desc: '561 Boom', kind: 'equipment', qty: 1, hrs: 8, rate: 95 },
      { desc: 'Operators Pick-up', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Buffing Rig', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: '4x4 Pickup', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Intermediate Operators', kind: 'labour', qty: 1, hrs: 12, rate: 75 },
      { desc: 'Spacers', kind: 'labour', qty: 1, hrs: 12, rate: 75 },
      { desc: 'Stabbers', kind: 'labour', qty: 1, hrs: 12, rate: 68 },
      { desc: 'Clampman', kind: 'labour', qty: 1, hrs: 12, rate: 60 },
      { desc: 'Labourers', kind: 'labour', qty: 3, hrs: 12, rate: 60 },
      { desc: 'Pressure Welder (rig)', kind: 'labour', qty: 5, hrs: 12, rate: 185, source: 'sub' },
    ],
    oneTime: [{ desc: 'Welding Rod', kind: 'equipment', qty: 1, hrs: 1, days: 1, rate: 5000, source: 'sub' }],
  },
  {
    name: 'Coating',
    prodPerDay: 1031,
    crew: [
      { desc: '4x4 Pickup', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Crew Cab', kind: 'equipment', qty: 3, hrs: 1, rate: 300 },
      { desc: '325 CFM Compressor', kind: 'equipment', qty: 1, hrs: 1, rate: 450 },
      { desc: 'Sandblast Unit', kind: 'equipment', qty: 1, hrs: 8, rate: 195 },
      { desc: 'Foreman', kind: 'labour', qty: 1, hrs: 12, rate: 90 },
      { desc: 'Sandblaster', kind: 'labour', qty: 1, hrs: 12, rate: 75 },
      { desc: 'Operator', kind: 'labour', qty: 1, hrs: 12, rate: 80 },
      { desc: 'Labourers', kind: 'labour', qty: 5, hrs: 12, rate: 60 },
      { desc: 'Coating Crew', kind: 'labour', qty: 1, hrs: 12, rate: 185, source: 'sub' },
    ],
    oneTime: [{ desc: 'Small Equipment (jeepers, testers, consumables)', kind: 'equipment', qty: 1, hrs: 1, days: 1, rate: 2000, source: 'sub' }],
  },
  {
    name: 'Ditch',
    prodPerDay: 581,
    conditions: 'Main ditch crew trenching.',
    crew: [
      { desc: '350/330 Hoe', kind: 'equipment', qty: 4, hrs: 8, rate: 175 },
      { desc: 'Operator Pick-up', kind: 'equipment', qty: 5, hrs: 1, rate: 300 },
      { desc: '4x4 Crew Cab', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Foreman', kind: 'labour', qty: 1, hrs: 12, rate: 90 },
      { desc: 'Operator', kind: 'labour', qty: 4, hrs: 12, rate: 80 },
      { desc: 'Labourer', kind: 'labour', qty: 2, hrs: 12, rate: 60 },
    ],
  },
  {
    name: 'Lower-in',
    prodPerDay: 977,
    crew: [
      { desc: '561 Boom', kind: 'equipment', qty: 3, hrs: 8, rate: 95 },
      { desc: 'Operator Pick-up', kind: 'equipment', qty: 3, hrs: 1, rate: 300 },
      { desc: '4x4 Crew Cab', kind: 'equipment', qty: 2, hrs: 1, rate: 300 },
      { desc: 'Foreman', kind: 'labour', qty: 1, hrs: 12, rate: 90 },
      { desc: 'Intermediate Operators', kind: 'labour', qty: 3, hrs: 12, rate: 75 },
      { desc: 'Labourers', kind: 'labour', qty: 5, hrs: 12, rate: 60 },
    ],
  },
  {
    name: 'Tie-ins',
    prodPerDay: 714,
    crew: [
      { desc: '572 Boom', kind: 'equipment', qty: 1, hrs: 8, rate: 105 },
      { desc: '561 Boom', kind: 'equipment', qty: 1, hrs: 8, rate: 95 },
      { desc: 'Operator Truck', kind: 'equipment', qty: 2, hrs: 1, rate: 300 },
      { desc: '4x4 Crew Cab', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Foreman', kind: 'labour', qty: 1, hrs: 12, rate: 90 },
      { desc: 'Intermediate Operators', kind: 'labour', qty: 2, hrs: 12, rate: 75 },
      { desc: 'Labourer', kind: 'labour', qty: 3, hrs: 12, rate: 60 },
      { desc: 'Welder', kind: 'labour', qty: 1, hrs: 12, rate: 185, source: 'sub' },
    ],
  },
  {
    name: 'Backfill',
    prodPerDay: 418,
    crew: [
      { desc: '350 Hoe', kind: 'equipment', qty: 1, hrs: 8, rate: 175 },
      { desc: 'D6T Dozer', kind: 'equipment', qty: 1, hrs: 8, rate: 165 },
      { desc: 'Operator Truck', kind: 'equipment', qty: 2, hrs: 1, rate: 300 },
      { desc: '4x4 Crew Cab', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Foreman', kind: 'labour', qty: 1, hrs: 12, rate: 90 },
      { desc: 'Labourers', kind: 'labour', qty: 2, hrs: 12, rate: 60 },
      { desc: 'Operators', kind: 'labour', qty: 2, hrs: 12, rate: 80 },
    ],
  },
  {
    name: 'Clean-up',
    prodPerDay: 523,
    crew: [
      { desc: 'D6T Dozer', kind: 'equipment', qty: 2, hrs: 8, rate: 165 },
      { desc: '350 Hoe', kind: 'equipment', qty: 2, hrs: 8, rate: 175 },
      { desc: 'Operator Truck', kind: 'equipment', qty: 4, hrs: 1, rate: 300 },
      { desc: '4x4 Crew Cab', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Operator', kind: 'labour', qty: 4, hrs: 12, rate: 80 },
      { desc: 'Labourer', kind: 'labour', qty: 2, hrs: 12, rate: 60 },
    ],
  },
  {
    name: 'Pig & Test',
    prodPerDay: 3093,
    conditions: 'Hydro test with 50/50 water/methanol; 3 days fluid rental allowed.',
    crew: [
      { desc: '4x4 Crew Cab', kind: 'equipment', qty: 2, hrs: 1, rate: 300 },
      { desc: 'Pressure Truck', kind: 'equipment', qty: 1, hrs: 10, rate: 300 },
      { desc: 'Fluid Truck', kind: 'equipment', qty: 12, hrs: 10, rate: 300 },
      { desc: 'Foreman', kind: 'labour', qty: 1, hrs: 12, rate: 90 },
      { desc: 'Labourers', kind: 'labour', qty: 3, hrs: 12, rate: 60 },
    ],
  },
  {
    name: 'Supervision',
    prodPerDay: 149,
    crew: [
      { desc: '4x4 Pickup (Superintendent)', kind: 'equipment', qty: 1, hrs: 1, rate: 300 },
      { desc: 'Superintendent', kind: 'labour', qty: 1, hrs: 12, rate: 115 },
      { desc: 'QA/QC Officer', kind: 'labour', qty: 1, hrs: 12, rate: 100 },
    ],
  },
];

const round1 = (n: number): number => Math.round(n * 10) / 10;

export interface ScopeOptions {
  profitPct?: number;
  /** Multiplier on crew/equipment quantities (bigger pipe = bigger crews). */
  crewScale?: number;
  /** Limit to the first N standard phases (smaller jobs). */
  maxPhases?: number;
}

export function buildWorkbookForScope(lengthM: number, opts: ScopeOptions = {}): BidWorkbook {
  const crewScale = opts.crewScale ?? 1;
  const profitPct = opts.profitPct ?? 30;
  const phases: BidPhase[] = [];

  // Mobilization / Demob — load-based, scales with project length.
  const loads = Math.max(8, Math.round(16 * (lengthM / 5230) * crewScale));
  phases.push({
    id: newId('ph'),
    name: 'Mobilization / Demobilization',
    distanceM: lengthM,
    productionPerDayM: Math.max(1, Math.round(lengthM / 3)),
    specialConditions: 'Based on good road conditions and full 24hr access.',
    items: [
      { id: newId('eq'), description: 'Mobilization — Legal Loads', kind: 'equipment', source: 'inhouse', qty: loads, hrs: 1, days: 1, rate: 1500 },
      { id: newId('eq'), description: 'Demobilization — Legal Loads', kind: 'equipment', source: 'inhouse', qty: loads, hrs: 1, days: 1, rate: 1500 },
    ],
  });

  const templates = opts.maxPhases ? STD_PHASES.slice(0, opts.maxPhases) : STD_PHASES;
  for (const tpl of templates) {
    const duration = round1(lengthM / tpl.prodPerDay);
    const items: BidLineItem[] = [];
    for (const c of tpl.crew) {
      items.push({
        id: newId(c.kind === 'labour' ? 'lab' : 'eq'),
        description: c.desc,
        kind: c.kind,
        source: c.source ?? 'inhouse',
        qty: Math.max(1, Math.round(c.qty * crewScale)),
        hrs: c.hrs,
        days: duration,
        rate: c.rate,
      });
    }
    for (const o of tpl.oneTime ?? []) {
      items.push({
        id: newId(o.kind === 'labour' ? 'lab' : 'eq'),
        description: o.desc,
        kind: o.kind,
        source: o.source ?? 'inhouse',
        qty: o.qty,
        hrs: o.hrs,
        days: o.days,
        rate: o.rate,
      });
    }
    phases.push({
      id: newId('ph'),
      name: tpl.name,
      distanceM: lengthM,
      productionPerDayM: tpl.prodPerDay,
      specialConditions: tpl.conditions ?? '',
      items,
    });
  }

  return { rowLengthM: lengthM, profitPct, phases };
}
